import React from "react";
import { Button as AntButton, type ButtonProps as AntButtonProps } from "antd";

export type ButtonVariant = "primary" | "ghost" | "dashed" | "link" | "text";

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'ghost' | 'variant'> {
  /** 视觉风格 */
  variant?: ButtonVariant;
  /** 是否加载中 */
  loading?: boolean;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<ButtonProps> & { __docgenInfo?: DocgenInfo };

/** 基础按钮，用于触发操作 */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  loading = false,
  children,
  ...rest
}) => {
  // 将自定义 variant 映射到 antd 的 type
  const getAntdType = (variant: ButtonVariant): AntButtonProps['type'] => {
    switch (variant) {
      case "primary":
        return "primary";
      case "ghost":
        return "default";
      case "dashed":
        return "dashed";
      case "link":
        return "link";
      case "text":
        return "text";
      default:
        return "primary";
    }
  };

  return (
    <AntButton
      {...rest}
      type={getAntdType(variant)}
      loading={loading}
      ghost={variant === "ghost"}
    >
      {children}
    </AntButton>
  );
};

// 手动添加 docgen 信息
(Button as ComponentWithDocgen).__docgenInfo = {
  displayName: "Button",
  description: "基础按钮，用于触发操作",
  props: {
    variant: {
      name: "variant",
      required: false,
      description: "视觉风格",
      type: { name: "ButtonVariant", raw: '"primary" | "ghost" | "dashed" | "link" | "text"' },
      defaultValue: { value: '"primary"' }
    },
    loading: {
      name: "loading",
      required: false,
      description: "是否加载中",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "false" }
    },
    children: {
      name: "children",
      required: false,
      description: "按钮内容",
      type: { name: "ReactNode", raw: "React.ReactNode" }
    },
    size: {
      name: "size",
      required: false,
      description: "按钮大小",
      type: { name: "ButtonSize", raw: '"small" | "middle" | "large"' }
    },
    disabled: {
      name: "disabled",
      required: false,
      description: "是否禁用",
      type: { name: "boolean", raw: "boolean" }
    },
    onClick: {
      name: "onClick",
      required: false,
      description: "点击事件",
      type: { name: "function", raw: "(event: MouseEvent) => void" }
    }
  }
};
